import { Flame, Image as ImageIcon, Newspaper, Video, Users, type LucideIcon } from "lucide-react";
import {
  SKU,
  imageVariant,
  label,
  labelRange,
  type PriceBook,
} from "@/entities/pricing/model/priceBook";
import {
  EXPLOSIVE_FULL_JOB_CREDITS_APPROX,
  VIDEO_DEFAULT_CLIP_SECONDS,
  VIDEO_DEFAULT_RESOLUTION,
  grouped,
  perImage,
  REPORTER_PER_ARTICLE_LABEL,
} from "@/entities/scene-card/model/creditRates";
import type { SceneHub } from "@/entities/scene-card/model/types";
import { moduleLabel } from "@/shared/lib/moduleLabel";
// 部门名单源(F1-b 提取到 entities/work):侧栏 / hub 页头 / 部门卡 / 作品卡都读这一处
import { DEPT_TITLE, DEPT_TITLE_KEY } from "@/entities/work/model/deptTitles";

/* ─────────────────────────────────────────────────────────────
 * 工作台目录 —— 纯数据 / helper(可单测,无 React 依赖)
 *
 * ★2026-07-24 F1-c:工作台收成 3 段后,「内容与服务」次级模块网格与右栏(快速开始 /
 * 即将上线)一并撤掉 —— 那两组入口在侧栏里本来就有,工作台再铺一遍是同一组入口的第二份。
 * 随之删掉 `moduleMeta` / `MODULE_META` / `DEPT_MODULE_KEYS` / `QUICK_START`(全部零引用)。
 *
 * ★2026-07-29(V25 军师回归)删掉 `INTELLIGENCE_ENTRIES`(军师对话 / 声量雷达 / 团队看板)及其
 *   `CuratedEntry` / `Gate` / `WbAction` / `WorkbenchGroup` 类型:2026-07-24 首页改 5 群卡带后
 *   没有任何页面再渲染这组 curated 卡,只剩它自己的单测在引用 —— 死数据被测试撑着,看上去还活着。
 *   这三项的新落点:军师对话 = hero/悬浮球,声量雷达 = 获客群卡,团队看板 = 情报群「经营看板」卡。
 * ───────────────────────────────────────────────────────────── */

/** chart>0 → chart token;否则回落 primary。喂给 style 的 `--ac`(部门卡 / 模块卡共用)。 */
export function identityVar(chart: number): string {
  return chart > 0 ? `var(--chart-${chart})` : "var(--primary)";
}

/**
 * B2 部门卡(终稿屏①「部门卡片墙」):四个 AI 部门,点卡直达各部门 hub。
 * 名称走 moduleLabel 单源(B3 改名「爆款情报」= 改常量一处全站生效);
 * 显隐按 moduleKey 走 visibleByPerms(组件里 useModuleVisible)。
 */
export interface DeptEntry {
  moduleKey: string;
  /** 对应的场景 hub —— 部门卡双人图 / hero 图都按它解析(`dept-<hub>.webp`)。 */
  hub: SceneHub;
  title: string;
  /** 部门名 i18n key(F3 补债);渲染点 `t(titleKey)`,title 为中文回退。 */
  titleKey: string;
  /** 副标(能力概览行,如「AI海报 · 14 个场景」)。 */
  tagline: string;
  desc: string;
  /** 场景 minis 样例标签带。 */
  minis: string[];
  /** 能力 chips。 */
  chips: string[];
  /**
   * 积分标 —— 由价目表现算(同 `SceneCard.costLabel`,理由见那里)。
   * 价目表没到货一律返空串,卡面渲染骨架:**不许兜一个可能过期的数**。
   */
  costLabel: (book: PriceBook) => string;
  route: string;
  chart: number;
  icon: LucideIcon;
}

/**
 * ★场景数**不在这里算**,交给 WorkbenchDeptCard 按权限现算。
 *
 * 存量缺陷(2026-07-23 卡片重构顺带修):分身部门卡副标写死「3 个场景」,而 `DH_CARDS`
 * 早已是 5 张(B5.5 加文生语音、E1 加照片数字人都没回来改这行)。写死常量与真源双写
 * 必然漂移 —— 记忆盘同型坑记过一次(「写死常量 vs 后端双源必漂」)。
 * 口径:只数 enabled **且当前用户点得进去**的卡 —— 副标是给用户的承诺,承诺里不能含
 * 他点不进去的卡(跨模块导流卡带 moduleKey,无该模块权限时卡墙里根本不渲染)。
 * 权限判定要 hook,常量表里做不了,故本文件只留模块名,数字在组件里补。
 */

export const DEPT_ENTRIES: DeptEntry[] = [
  {
    moduleKey: "ai_poster",
    hub: "poster",
    title: DEPT_TITLE.poster,
    titleKey: DEPT_TITLE_KEY.poster,
    tagline: moduleLabel("ai_poster"),
    // ★2026-07-31:卡墙改版把舱内卡收到 6 张,简介跟着收窄 —— **简介必须跟着卡墙走**,
    //   不能许诺点进去找不到的东西。
    // ★2026-08-05 最终版:企业介绍 / 活动海报 / 榜单 / 横宽四张放回墙上(「对外宣传」组)。
    // ★2026-08-12:横宽宽图收起(POSTER_HIDDEN_CARD_KEYS)→ 对外宣传剩 3 种;同日换衣卡
    //   补进该组接替横宽的位子 → 回到 4 种。两次变更之间 desc 短暂写过 3 种,
    //   这里跟到最终态。**没有任何测试锁这个数字**(desc 是自由文本)——它靠人记得改,
    //   所以把「谁把它变成 4 的」写在这里,下次收/放卡时照着核。
    //   ★这句**不逐一列举八张卡的全名**:desc 是 `line-clamp-2`,实测这张卡两行只装得下
    //   ~30 字符(e2e 量的:列全名要 67 字符 = 4 行,后两行连同「对外宣传」那四类一起被截掉,
    //   等于白写)。所以 desc 只说**结构**(两组各 4 种),具体叫什么交给下面的 minis 标签带;
    //   原来结尾那句「二维码 / LOGO 确定性合成」降级进 chips —— 承诺没丢,只是换了个位置说。
    //   量表另记:六张部门卡的 desc **全部**超两行(42~48 字要 3 行),这是存量常态,不在本波修。
    desc: "上架卖货 4 种 + 对外宣传 4 种,也可自己描述或拿图改。",
    // minis 取两组各自的代表(卖货 2 + 对外宣传 2),不是把八张全铺上来
    minis: ["商品主图", "爆点组图", "详情长图", "活动海报"],
    chips: ["文字生图", "图改图", "一次三比例", "二维码 / LOGO"],
    // ★「起」价取**最便宜档 1K**(0828 用户拍板):这里是「约 X 积分/张起」——起=下限,
    //   下限就得是价目表里真实的最低档(openai_1K=965),不是默认档(2K=1448)。
    //   「默认 2K」的语义在创建页/确认卡里由真估算器表达,部门卡只承诺下限。
    costLabel: (b) =>
      label(
        b.unit(SKU.posterImage, imageVariant("openai", "1K")),
        (c) => `出图${perImage(c)}起(1K 起步档)`
      ),
    route: "/ai-haibao",
    chart: 3,
    icon: ImageIcon,
  },
  {
    moduleKey: "ai_video",
    hub: "video",
    title: DEPT_TITLE.video,
    titleKey: DEPT_TITLE_KEY.video,
    tagline: moduleLabel("ai_video"),
    desc: "叙事 / 硬广双档,一键追爆与爆款仿拍。产品图做首帧防糊字,导演台逐镜可改。",
    minis: ["叙事短视频", "硬广大片", "一键追爆"],
    chips: ["文生视频", "产品图首帧", "参考生视频"],
    // B2 #9(0927 K4 实测):直发路(自由创作)不出台本图,实扣 475(video.render 一行);旧「约 565
    //   (含 1 张台本图)」把编排流的台本图算进了直发价。部门卡报**自由创作直发**口径,目的片另有编排费
    //   (目的卡各自标「编排通常 100~500(最高 1,900)+ 成片按秒」)。每秒价来自价目表,取不到不出数。
    costLabel: (b) => {
      const perSec = b.unit(SKU.video, `seedance20fast_${VIDEO_DEFAULT_RESOLUTION}`);
      if (perSec == null) return "";
      return `自由创作 ${VIDEO_DEFAULT_CLIP_SECONDS} 秒 ${VIDEO_DEFAULT_RESOLUTION.toUpperCase()} 约 ${grouped(perSec * VIDEO_DEFAULT_CLIP_SECONDS)} 积分(直发);目的片另计编排`;
    },
    route: "/ai-video",
    chart: 4,
    icon: Video,
  },
  {
    moduleKey: "ai_digital_human",
    hub: "dh",
    title: DEPT_TITLE.dh,
    titleKey: DEPT_TITLE_KEY.dh,
    tagline: moduleLabel("ai_digital_human"),
    desc: "口播出镜 + 产品插播画面标记,朗读 10 秒以上(录到 19 秒自动停)即可克隆专属音色。口播公式一键套真产品。",
    // 与分身舱卡墙逐字同名(2026-08-13 改名:口播成片 → 视频数字人)—— 工作台上写的名字
    // 点进去必须找得到同名的卡,否则用户以为少了一个能力。
    minis: ["视频数字人", "照片数字人", "文生语音", "声纹克隆"],
    chips: ["出镜", "插播画面", "声纹"],
    // ★舱级报价给**区间**:这一舱四条线单价差 90 倍(纯音频 10 / 照片省流 20 / 视频换口播 80 /
    // 照片旗舰 500~900)。原来只写 flagship 一档,而卡墙第一张卡走的是 80 那档 —— 舱口报一个
    // 谁都对不上的数,不如把范围讲清,具体单价各卡自己标。
    // ★舱级报价给**区间**,而且是**整张价目表现算的区间**(不是手挑两档):
    //   手挑的话,新增一个更贵/更便宜的档时这句话会静默失真 —— 原来写死的
    //   「10~1000」正是这么来的(那还是 ×10 时代的数,实为 2~160)。
    costLabel: (b) =>
      labelRange(b.range(SKU.dhRender), (lo, hi) => `合成 ${lo}~${hi} 积分 / 秒(按档位)`),
    route: "/ai-digital-human",
    chart: 6,
    icon: Users,
  },
  {
    moduleKey: "ai_explosive_parse",
    hub: "explosive",
    title: DEPT_TITLE.explosive,
    titleKey: DEPT_TITLE_KEY.explosive,
    // 2026-07-31 卡墙改版:原来这里是「解析 + 蓝图库 · 输入源」—— 说的是它的**构造**,
    // 别的舱副标说的都是「这是个什么 AI」。统一成同一种句式。
    tagline: "AI爆款复刻",
    desc: "拆一条爆款为你所用:结构 / 爆点 / 口播公式落成蓝图,辐射视频 · 口播 · 海报三端。",
    minis: ["拆解报告", "蓝图库", "路由目的地"],
    chips: ["只学结构", "用完即删", "溯源"],
    costLabel: () => `取片+解析 ${EXPLOSIVE_FULL_JOB_CREDITS_APPROX} 积分 · 蓝图复用不扣`,
    route: "/ai-explosive",
    chart: 5,
    icon: Flame,
  },
  {
    moduleKey: "ai_reporter",
    hub: "reporter",
    title: DEPT_TITLE.jizhe,
    titleKey: DEPT_TITLE_KEY.jizhe,
    tagline: moduleLabel("ai_reporter"),
    desc: "以企业知识库为底的 GEO 推广图文成稿:拓词覆盖、规划可确认、质量守卫四维评分,导出公众号 / Word。",
    minis: ["GEO 问答文", "企业新闻", "产品软文", "种草笔记"],
    chips: ["知识留痕", "拓词覆盖", "质量守卫"],
    costLabel: () => `成稿${REPORTER_PER_ARTICLE_LABEL}(按实际消耗结算;1200 字档实测,长文上浮)`,
    route: "/ai-jizhe",
    chart: 2,
    icon: Newspaper,
  },
];
